import React, { useEffect } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';

// bonnet
import { useDevice } from '@bonnet/next/device';

import { useFeatures } from '@atc/features';
import { formatCurrency } from '@atc/string-fns';

import {
    Heading,
    ResponsiveImage,
    Section,
} from 'reaxl';
import {
    InfoIcon,
    InventoryImagePlaceholder,
} from 'reaxl-molecules';

import {
    kbbVRSDataDuck,
    preorderDuck,
    queryDuck,
} from '@/ducks';

import {
    bapSelectedOptionsDuck,
} from '@/ducks/bap';

// containers
import BuildAndPriceBodyStylePlaceholder from './BuildAndPriceBodyStylePlaceholder';
import BuildAndPriceMainImagePlaceholder from './BuildAndPriceMainImagePlaceholder';
import BuildAndPriceOptionsContainer from './BuildAndPriceOptionsContainer';
import BuildAndPricePaginationContainer from './BuildAndPricePaginationContainer';
import BuildAndPricePaymentContainer from './BuildAndPricePaymentContainer';

/*
 * Parent container for Build & Price components
 */
export const BuildAndPriceContainer = React.memo(() => {
    const dispatch = useDispatch();
    const {
        preorder: [enablePreorder],
    } = useFeatures([
        'preorder',
    ]);
    const query = useSelector(queryDuck.selectors.getDuckState);
    const kbbVRSData = useSelector(kbbVRSDataDuck.selectors.getData);
    const loading = useSelector(kbbVRSDataDuck.selectors.getLoading);
    const optionsData = useSelector(kbbVRSDataDuck.selectors.getOptionsData);
    const selectedOptions = useSelector(bapSelectedOptionsDuck.selectors.getDuckState);
    const trimInfo = useSelector(kbbVRSDataDuck.selectors.getSelectedTrimInfo);
    const trimsData = useSelector(kbbVRSDataDuck.selectors.getSelectedMakeModelTrims);
    const owner = _get(query, ['owners', query.ownerId], {});
    const { make, model, year, trimName, nationalMSRP, imagePath, driveTypeInfo = {} } = trimInfo || {};
    const device = useDevice();
    const isXs = _get(device, 'is.xs', false);

    const makeCodeList = query?.makeCode || query?.makeCodeList;
    const makeCode = !Array.isArray(makeCodeList) && makeCodeList;

    const modelCodeList = query?.modelCode || query?.modelCodeList;
    const modelCode = !Array.isArray(modelCodeList) && modelCodeList;

    const seriesCodeList = query?.seriesCode || query?.seriesCodeList;
    const seriesCode = !Array.isArray(seriesCodeList) && seriesCodeList;

    /*
     * If body style section is enabled - fetch all models to put into the body style tabs - but only fetch all trims
     * for selected model
     * If body style section is not enabled - just fetch all trims for the make model in query
     */
    useEffect(() => {
        if (enablePreorder && makeCode) {
            dispatch(kbbVRSDataDuck.creators.loadVrsDataWithModels({
                makeCode,
                selectedModel: modelCode || seriesCode || '',
                selectedYear: query?.year,
            }));
        }
    }, [dispatch, enablePreorder, makeCode, modelCode, query?.year, seriesCode]);

    useEffect(() => {
        const preorderListing = {
            preorderBAP: {
                pricingDetail: {
                    salePrice: nationalMSRP,
                },
                owner,
                id: 'preorderBAP',
                type: 'new',
                make,
                model,
                year,
            },
        };
        dispatch(preorderDuck.creators.addPreorder(preorderListing));
    }, [dispatch, make, model, nationalMSRP, owner, year]);

    // reset active options/selections every time the page unmounts
    useEffect(() => () => {
        dispatch(bapSelectedOptionsDuck.creators.reset());
    }, [dispatch]);

    const rootClassName = 'padding-bottom-4 padding-bottom-sm-0 padding-top-5 padding-top-sm-6 padding-top-md-7'
        + ' padding-left-sm-0 padding-right-sm-2 padding-right-md-5 padding-right-lg-7';

    const renderPricing = () => {
        if (!nationalMSRP) {
            return (
                <span className="text-size-600 text-normal">
                    Contact Dealer For Price
                </span>
            );
        }

        const selectedDriveTrain = selectedOptions?.availableDriveTrainList?.optionName ?? Object.keys(driveTypeInfo ?? {})[0];
        const ncbbPrice = driveTypeInfo?.[selectedDriveTrain]?.ncbbPrice;
        const basePrice = ncbbPrice && ncbbPrice > 0 ? ncbbPrice : nationalMSRP;

        const total = Object.keys(selectedOptions || {}).reduce((price, optionKey) => {
            const selectedOption = selectedOptions[optionKey];
            if (selectedOption) {
                const { msrp } = selectedOption;
                if (Array.isArray(selectedOption)) {
                    return price + selectedOption.reduce((listPrice, currentItem) => (listPrice + (currentItem.msrp ? currentItem.msrp : 0)), 0);
                }
                if (msrp) {
                    return price + msrp;
                }
            }
            return price;
        }, basePrice);

        return (
            <>
                <div className="margin-vertical-1">
                    <span className="text-size-600 text-normal">{formatCurrency(total)}</span>
                    <BuildAndPricePaymentContainer
                        nationalMSRP={total}
                    />
                </div>
                <InfoIcon
                    infoText="MSRP excludes tax, title, license, delivery, processing & handling fees and optional equipment. Contact dealer for details."
                    className="margin-left-1"
                    placement="bottom"
                />
                <span className="text-subdued-lightest text-size-200">Starting MSRP before options</span>
            </>
        );
    };

    const titleSection = (
        <>
            <Heading
                size={700}
                weight="normal"
                id="build-and-price-heading"
            >
                {year}
                {' '}
                <strong>
                    {make}
                    {' '}
                    {model}
                </strong>
                {' '}
                {trimName}
            </Heading>
            <div className="margin-vertical-1">
                {renderPricing()}
            </div>
            <div className={rootClassName}>
                {imagePath
                    ? <ResponsiveImage src={imagePath} />
                    : <InventoryImagePlaceholder className="padding-6" />}
            </div>
        </>
    );

    const renderBuildAndPriceOptionsContainer = () => !_isEmpty(kbbVRSData) && !loading && (
        <BuildAndPriceOptionsContainer
            device={device}
            optionsData={optionsData}
            trimsData={trimsData}
        />
    );

    const heading = _isEmpty(kbbVRSData) ? 'No Vehicles to Preorder' : 'Select Your Preferences';

    return (
        <>
            <BuildAndPricePaginationContainer />
            <div className="container">
                <div className="row">
                    <div
                        className="col-xs-12 col-sm-6 col-md-7 col-lg-8 margin-top-4 margin-top-sm-1 padding-horizontal-5 padding-vertical-4"
                        style={!isXs ? { position: 'sticky', top: '0' } : {}}
                    >
                        {loading
                            ? <BuildAndPriceMainImagePlaceholder />
                            : (!_isEmpty(trimInfo) && titleSection)}
                    </div>
                    <hr className="col-xs-12 display-sm-none padding-0" />
                    <div
                        className="col-xs-12 col-sm-6 col-md-5 col-lg-4 padding-horizontal-0"
                        style={{ borderLeft: '1px solid #e1e4e8', minHeight: '200px' }}
                    >
                        {loading ? <BuildAndPriceBodyStylePlaceholder />
                            : (
                                <Section
                                    alignment="left"
                                    data-cmp="cntnr-prf-opt"
                                    heading={heading}
                                    headingClassName="margin-horizontal-5 text-blue-darker text-size-500 margin-top-4 margin-top-sm-5"
                                    uiContext="section-3"
                                >
                                    {renderBuildAndPriceOptionsContainer()}
                                </Section>
                            )}
                    </div>
                </div>
            </div>
        </>
    );
});

export default BuildAndPriceContainer;
